import Logo from '@assets/images/logo-dark.svg'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router'
import { LangSelectionModal } from '@/components/commons/lang-selection-modal'
import { UserMenu, type UserMenuAction } from '@/components/commons/user-menu'
import { Container } from '@/components/ui/container'
import { LogoutIcon } from '@/components/ui/icons'
import { logout } from '@/features/auth/store/actions.ts'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface HeaderBarProps {
	endSlot?: ReactNode
	className?: string
	/**
	 * Who is signed in, handed in by whoever knows. The bar reads no store and
	 * asks no endpoint: there are two identities in this app, the Luminate
	 * reviewer and the Marquee operator, and each section supplies its own
	 * (`Header`, `MarqueeHeader`).
	 *
	 * `roleLabel` arrives translated for the same reason. The two vocabularies
	 * are unrelated (`marketing_manager` and friends on one side, `creator` /
	 * `facilitator` on the other) and only the caller knows which one its role
	 * belongs to. Absent means the menu shows the name alone.
	 */
	user?: {
		name: string
		email: string
		roleLabel?: string
	}
	/** Where the logo goes back to, which is per section. */
	homeTo: string
	/**
	 * Section-specific entries, listed above Sign out.
	 *
	 * The bar owns signing out and nothing else, so anything that is only
	 * meaningful inside one section (Marquee's host lookup table) is passed in
	 * by that section's adapter rather than branched on here.
	 */
	menuActions?: UserMenuAction[]
}

/**
 * The app header: logo, an optional slot, and the user menu.
 *
 * Presentational on purpose. Signing out is the exception it still owns, since
 * there is one session behind both identities until Marquee gets its own login
 * (`POST /marquee/auth/login`, story 6.4). When that lands, the action becomes
 * a prop like the rest.
 */
export const HeaderBar = ({
	endSlot,
	className,
	user,
	homeTo,
	menuActions = []
}: HeaderBarProps) => {
	const { t } = useTranslation()
	const [showLangModal, setShowLangModal] = useState(false)

	return (
		<header className={cn('box-border h-16 bg-neutral-white', className)}>
			<Container fullWidth>
				<div className='flex h-full items-center justify-between'>
					<Link aria-description='Go to the index page' to={homeTo}>
						<img alt={'logo'} height={28} src={Logo} width={118} />
					</Link>
					<div className='flex justify-end gap-4'>
						{endSlot}

						{user && (
							<UserMenu
								actions={[
									...menuActions,
									{
										label: t('common:logout'),
										icon: <LogoutIcon size={16} />,
										onClickCb: logout
									}
								]}
								user={{ ...user, imageUrl: '' }}
							/>
						)}
					</div>
				</div>
			</Container>
			{showLangModal && (
				<LangSelectionModal onCloseCb={() => setShowLangModal(false)} />
			)}
		</header>
	)
}

HeaderBar.displayName = 'HeaderBar'
